/*
 * PDP size finder
 *
 * Three questions (smaller side, cup difference, usual bra size) resolve to a
 * product variant: option1 is the EU regular size ("80C"), option2 is the
 * smaller side plus the difference code ("R2" = 1 cup, "R3" = 2 cups), the
 * same encoding the detailed calculator in sym-calc-v2 produces.
 *
 * On a match the theme's own <variant-selects> is driven so Dawn handles the
 * price, URL and add-to-cart state. No match, or no stock, shows an error.
 *
 * A size that is sold out or not made offers at most two in-stock sister
 * sizes from Merlin's table (snippets/size-data.liquid), never changing the
 * support level. "3+ cups" shows a tip whose link opens the calculator:
 * opening it straight away confused people who could see its card (Merlin, 2026-09-23).
 * It is also an answer: with her band and cup it is a size we do not make
 * (75B L4+), and the waitlist box asks for her email straight away, sending the
 * size she gave so she never has to enter it again (Merlin, 14.10).
 *
 * A size, once found, is kept in this browser (Kaisa, 17.09): coming back to the
 * product page restores the answers without logging them again, and the bar in
 * snippets/size-bar.liquid carries it, with Add to cart, onto every page.
 *
 * Every answer is reported to Meta and to Shopify's customer events (track),
 * and every size is logged to the results sheet. On any theme but the
 * published one, rows are tagged source=test and Klaviyo signups are skipped
 * unless the address contains "+test", so previews never pollute the data.
 */
class SizeFinder extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-finder-variants]');
    this.variants = data ? JSON.parse(data.textContent) : [];
    this.sizeData = this.readSizeData();

    this.state = { side: null, diff: null, band: '', cup: '' };

    this.steps = Array.from(this.querySelectorAll('[data-finder-step]'));
    this.chips = Array.from(this.querySelectorAll('[data-finder-chip]'));
    /* Keep the clean label so the completed tick can be prefixed and removed. */
    this.chips.forEach((chip) => {
      chip.dataset.label = chip.textContent.trim();
      /* Kaisa's 17.09 ask: the step tabs move between questions, not only Back. */
      chip.addEventListener('click', () => this.goToStep(Number(chip.dataset.finderChip)));
    });
    this.backEl = this.querySelector('[data-finder-back]');
    this.measureEl = this.querySelector('[data-finder-measure]');

    /* Matches the mobile breakpoint in pdp-redesign.css; keep them in sync. */
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.step = 1;

    /* Groups this visitor's size selections in the results log, the same way
       the detailed calculator generates its xid. */
    this.xid =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);

    /* "main" is the published theme; previews and development copies differ. */
    this.isLiveTheme = Boolean(
      window.Shopify && window.Shopify.theme && window.Shopify.theme.role === 'main'
    );

    this.resultEl = this.querySelector('[data-finder-result]');
    this.errorEl = this.querySelector('[data-finder-error]');
    this.errorSizeEl = this.querySelector('[data-finder-error-size]');
    this.errorHeadingEl = this.querySelector('[data-finder-error-heading]');
    this.errorNoteEl = this.querySelector('[data-finder-error-note]');
    this.sistersEl = this.querySelector('[data-finder-sisters]');
    this.notifyFormEl = this.querySelector('[data-finder-notify-form]');
    this.notifyMessageEl = this.querySelector('[data-finder-notify-message]');
    this.emailEl = this.querySelector('[data-finder-email]');
    this.notifyEl = this.querySelector('[data-finder-notify]');
    this.errorMeasureEl = this.querySelector('[data-finder-error-measure]');
    if (this.errorMeasureEl) {
      this.errorMeasureEl.addEventListener('click', () => this.openCalculator());
    }
    this.signatureEl = this.querySelector('[data-finder-error-signature]');
    this.communityEl = this.querySelector('[data-finder-community]');

    if (this.notifyEl) {
      this.notifyEl.addEventListener('click', () => this.submitNotify());
    }
    if (this.emailEl) {
      this.emailEl.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.submitNotify();
        }
      });
    }
    this.euEl = this.querySelector('[data-finder-eu]');
    this.usEl = this.querySelector('[data-finder-us]');
    this.bandSelect = this.querySelector('[data-finder-band]');
    this.cupSelect = this.querySelector('[data-finder-cup]');
    this.fillSelects();

    this.querySelectorAll('[data-finder-side]').forEach((button) => {
      button.addEventListener('click', () => {
        this.trackStart('side', button.dataset.finderSide);
        this.state.side = button.dataset.finderSide;
        this.selectPill(button, '[data-finder-side]');
        this.resolve();
        this.advance(2);
      });
    });

    this.querySelectorAll('[data-finder-diff]').forEach((button) => {
      button.addEventListener('click', () => {
        this.trackStart('difference', button.dataset.finderDiff);
        this.state.diff = button.dataset.finderDiff;
        this.selectPill(button, '[data-finder-diff], [data-finder-unsure]');
        this.hide(this.unsureNoteEl);
        this.show(this.measureEl);
        this.resolve();
        this.advance(3);
      });
    });

    this.unsureEl = this.querySelector('[data-finder-unsure]');
    this.unsureNoteEl = this.querySelector('[data-finder-unsure-note]');
    const inlineMeasure = this.querySelector('[data-finder-measure-inline]');
    if (inlineMeasure) {
      inlineMeasure.addEventListener('click', () => this.openCalculator());
    }
    if (this.unsureEl) {
      this.unsureEl.addEventListener('click', () => this.notSure());
    }

    if (this.backEl) {
      this.backEl.addEventListener('click', () => this.goToStep(this.step - 1));
    }

    if (this.measureEl) {
      this.measureEl.addEventListener('click', () => this.openCalculator());
    }

    this.goToStep(1);

    [this.bandSelect, this.cupSelect].forEach((select) => {
      if (!select) return;
      select.addEventListener('change', () => {
        this.trackStart(select === this.bandSelect ? 'band' : 'cup', select.value);
        this.state.band = this.bandSelect.value;
        this.state.cup = this.cupSelect.value;
        this.resolve();
      });
    });

    this.watchManualPicker();
    this.watchWhatsApp();
    this.watchEmailSize();
    this.syncBuyVisibility();

    /*
     * Restoring drives the theme's variant change, which calls the pickup
     * availability element. Its script loads after this one, and until it has
     * defined the element that call throws: after Add to cart was disabled and
     * before it is enabled again, so a returning shopper found it greyed out
     * (seen live on 2026-09-23). Wait for the definition when the element is on
     * the page and not defined yet.
     */
    const pickup = document.querySelector('pickup-availability');
    if (pickup && !customElements.get('pickup-availability')) {
      customElements.whenDefined('pickup-availability').then(() => this.restoreSize());
    } else {
      this.restoreSize();
    }
  }

  /*
   * One event to Meta and one to Shopify's customer events. Each is guarded on
   * its own, so a blocked or missing pixel can never stop the finder working.
   */
  track(name, params) {
    try {
      if (typeof window.fbq === 'function') window.fbq('trackCustom', name, params);
    } catch (error) {
      /* the pixel is optional */
    }
    try {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') analytics.publish(name, params);
    } catch (error) {
      /* so are customer events */
    }
  }

  /* size_finder_start fires once per page view, on the first answer. */
  trackStart(question, answer) {
    if (this.started) return;
    this.started = true;
    this.track('size_finder_start', { question: question, answer: answer });
  }

  watchWhatsApp() {
    const section = document.getElementById(`MainProduct-${this.dataset.section}`);
    (section || document).querySelectorAll('a.pdp-whatsapp').forEach((link) => {
      link.addEventListener('click', () => {
        this.track('whatsapp_click', { size: this.isComplete ? this.fullSize : '' });
      });
    });
  }

  /*
   * The hidden properties[_size_source] input in the product form: "finder"
   * when the finder chose the variant, "manual" when the dropdowns did. It
   * travels with the line item onto the order.
   */
  setSizeSource(source) {
    const form = document.getElementById(`product-form-${this.dataset.section}`);
    const input = form && form.querySelector('input[name="properties[_size_source]"]');
    if (input) input.value = source;
  }

  /*
   * The band and cup lists and Merlin's sister-size table, from
   * snippets/size-data.liquid. If someone saves that file as broken JSON the
   * finder keeps working on the built-in grid and simply suggests nothing.
   */
  readSizeData() {
    const el = this.querySelector('[data-size-data]');
    try {
      const data = el ? JSON.parse(el.textContent) : null;
      if (data && Array.isArray(data.bands) && Array.isArray(data.cups)) return data;
    } catch (error) {
      console.error('Size finder: snippets/size-data.liquid is not valid JSON', error);
    }
    return SizeFinder.FALLBACK_SIZE_DATA;
  }

  fillSelects() {
    if (this.bandSelect && this.bandSelect.options.length <= 1) {
      this.sizeData.bands.forEach((band) => {
        const option = new Option(`${band.eu} / ${band.us}`, band.eu);
        option.dataset.us = band.us;
        this.bandSelect.add(option);
      });
    }
    if (this.cupSelect && this.cupSelect.options.length <= 1) {
      // "Cup", or "Korv" on the Estonian site (snippets/pdp-text.liquid).
      const label = this.dataset.cupLabel || 'Cup';
      this.sizeData.cups.forEach((cup) => {
        this.cupSelect.add(new Option(cup.note ? `${label} ${cup.eu} (${cup.note})` : `${label} ${cup.eu}`, cup.eu));
      });
    }
  }

  /*
   * Merlin's rules (15.09): the support level (option2) never changes, only
   * in-stock variants, at most two, all of tier 1 before tier 2 before tier 3.
   */
  sisterSizes() {
    const table = (this.sizeData.sister_sizes || {})[this.regularSize];
    if (!table) return [];
    const picks = [];
    ['tier1', 'tier2', 'tier3'].forEach((tier) => {
      (table[tier] || []).forEach((size) => {
        if (picks.length >= 2) return;
        const variant = this.variants.find(
          (v) => v.o1 === size && v.o2 === this.differenceCode && v.available
        );
        if (variant) picks.push({ variant: variant, tier: tier });
      });
    });
    return picks;
  }

  /* The sentence under the unavailable size: what happened, then the best
     suggestion, or where she stands if there is none. */
  unavailableNote(reason, picks) {
    const fill = (text, sister) =>
      (text || '').replace('[size]', this.fullSize).replace('[sister]', sister || '');
    if (this.threePlusEmail && this.dataset.errorThreePlus) return fill(this.dataset.errorThreePlus);
    if (!picks.length) {
      return fill(reason === 'not_produced' ? this.dataset.errorMissing : this.dataset.errorSoldOut);
    }
    const best = picks[0].variant;
    const sister = best.o1 + ' ' + best.o2;
    const lead = fill(reason === 'not_produced' ? this.dataset.leadMissing : this.dataset.leadSoldOut);
    let detail;
    if (picks[0].tier === 'tier3') {
      detail = this.cupIndex(best.o1) > this.cupIndex(this.regularSize)
        ? this.dataset.sisterRoomier
        : this.dataset.sisterSmaller;
    } else {
      detail = parseInt(best.o1, 10) < parseInt(this.regularSize, 10)
        ? this.dataset.sisterFirmer
        : this.dataset.sisterLooser;
    }
    return (lead + ' ' + fill(detail, sister)).trim();
  }

  cupIndex(size) {
    const letter = String(size).replace(/^\d+/, '');
    return this.sizeData.cups.findIndex((cup) => cup.eu === letter);
  }

  renderSisters(picks) {
    if (!this.sistersEl) return;
    this.sistersEl.innerHTML = '';
    picks.forEach((pick) => {
      const size = pick.variant.o1 + ' ' + pick.variant.o2;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pdp-pill pdp-oos__sister';
      button.setAttribute('aria-pressed', 'false');
      button.textContent = (this.dataset.sisterButton || 'Choose [sister]').replace('[sister]', size);
      button.dataset.variantId = pick.variant.id;
      button.addEventListener('click', () => this.chooseSister(pick, button));
      this.sistersEl.appendChild(button);
    });
    this.sistersEl.hidden = picks.length === 0;
  }

  /* A sister size is a deliberate choice: select it and let her buy it. */
  chooseSister(pick, button) {
    this.sisterVariant = pick.variant;
    this.selectPill(button, '.pdp-oos__sister');
    this.applyVariant(pick.variant.o1, pick.variant.o2, 'finder_sister');
    if (!this.restoring) {
      this.track('sister_size_chosen', {
        size: this.fullSize,
        sister: pick.variant.o1 + ' ' + pick.variant.o2,
        tier: pick.tier
      });
    }
    this.saveSize(pick.variant, true);
    this.syncBuyVisibility();
  }

  /* "3+ cups": log the choice and point at the calculator, which she opens
     herself from the card (desktop) or the tape link (mobile). With her band
     and cup it resolves to a size we do not make, so the waitlist box shows. */
  notSure() {
    this.trackStart('difference', 'not_sure');
    this.state.diff = SizeFinder.THREE_PLUS;
    this.selectPill(this.unsureEl, '[data-finder-diff], [data-finder-unsure]');
    this.show(this.unsureNoteEl);
    /* The tip carries the tape link on mobile, so the separate one would say it twice. */
    this.hide(this.measureEl);
    this.logRow({ gap_choice: 'not_sure' });
    this.track('size_finder_not_sure', {});
    /* 3+ cups is no size to carry around: the one she had is forgotten, and this one is never kept. */
    SizeFinder.clearSaved();
    this.resolve();
    this.advance(3);
  }

  get isThreePlus() {
    return this.state.diff === SizeFinder.THREE_PLUS;
  }

  /* The 3+ note, button and thanks promise her size by email, which only the
     waitlist event's flow sends; with no event name set, 3+ reads as any size
     we do not make. */
  get threePlusEmail() {
    return Boolean(this.isThreePlus && this.dataset.notifyMetric);
  }

  /* The calculator modal exists only where the English calculator is mounted;
     anywhere else the calculator page is the way in. */
  openCalculator() {
    if (document.getElementById('sym-calculator')) {
      window.dispatchEvent(new Event('openCalculatorModal'));
      return;
    }
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window.location.href = root + 'pages/size-calculator';
  }

  get variantSelects() {
    return document.querySelector(
      `variant-selects[data-section="${this.dataset.section}"], variant-radios[data-section="${this.dataset.section}"]`
    );
  }

  get buyWrapper() {
    const section = document.getElementById(`MainProduct-${this.dataset.section}`);
    return (section || document).querySelector('.pdp-buy--gated');
  }

  /*
   * The collapsed "Choose size manually" dropdowns are the escape hatch, so a
   * deliberate pick there must also reveal the buy button. Changes the finder
   * itself makes are flagged, so they don't count as a manual override.
   */
  watchManualPicker() {
    const selects = this.variantSelects;
    if (!selects) return;

    selects.addEventListener('change', () => {
      if (!this.applyingVariant) {
        this.manualOverride = true;
        this.setSizeSource('manual');
      }
      /* Let Dawn's own listener resolve currentVariant first. */
      setTimeout(() => this.syncBuyVisibility(), 0);
    });
  }

  /*
   * Single source of truth for whether the buy button is on screen: a manual
   * pick, a chosen sister size or the finder's own answer has to land on a
   * variant that is in stock. A manual pick counts even while the finder shows
   * an unavailable size; it is the escape hatch.
   */
  syncBuyVisibility() {
    const wrapper = this.buyWrapper;
    if (!wrapper) return;

    let ready = false;

    if (this.manualOverride) {
      const current = this.variantSelects && this.variantSelects.currentVariant;
      ready = Boolean(current && current.available);
    } else if (this.sisterVariant) {
      ready = Boolean(this.sisterVariant.available);
    } else if (!this.hasError && this.isComplete) {
      const match = this.matchVariant();
      ready = Boolean(match && match.available);
    }

    wrapper.classList.toggle('is-ready', ready);
    this.syncEmailSize(ready);
  }

  /* The add button stays shut while an unavailable size is all there is. */
  get isLocked() {
    return Boolean(this.hasError && !this.sisterVariant && !this.manualOverride);
  }

  matchVariant() {
    return this.variants.find(
      (variant) => variant.o1 === this.regularSize && variant.o2 === this.differenceCode
    );
  }

  /* Answering a question moves to the next one, but only in the one-at-a-time
     mobile layout, and never backwards if they are revising an earlier answer. */
  advance(step) {
    if (!this.mobileQuery.matches) return;
    if (step <= this.step) return;
    this.goToStep(step);
  }

  goToStep(step) {
    this.step = Math.min(Math.max(step, 1), this.steps.length);
    this.maxStep = Math.max(this.maxStep || 1, this.step);

    this.steps.forEach((el) => {
      el.classList.toggle('is-active', Number(el.dataset.finderStep) === this.step);
    });

    this.chips.forEach((chip) => {
      const index = Number(chip.dataset.finderChip);
      const done = index < this.step;
      chip.classList.toggle('pdp-finder__chip--active', index === this.step);
      chip.classList.toggle('pdp-finder__chip--done', done);
      chip.textContent = done ? '✓ ' + chip.dataset.label : chip.dataset.label;
      /* Any step already reached can be tapped, forward as well as back. */
      chip.disabled = index > this.maxStep;
    });

    if (this.backEl) this.backEl.hidden = this.step === 1;
  }

  selectPill(active, selector) {
    this.querySelectorAll(selector).forEach((button) => {
      const isActive = button === active;
      button.classList.toggle('pdp-pill--active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  get isComplete() {
    const { side, diff, band, cup } = this.state;
    return Boolean(side && diff && band && cup);
  }

  /* "80" + "C" -> "80C", matching variant option1 */
  get regularSize() {
    return this.state.band + this.state.cup;
  }

  /* "R" + "2" -> "R2", matching variant option2 */
  get differenceCode() {
    return this.state.side + this.state.diff;
  }

  /* "80C R2": what the customer actually asked for, used in messages */
  get fullSize() {
    return this.regularSize + ' ' + this.differenceCode;
  }

  get usSize() {
    const option = this.bandSelect.querySelector(`option[value="${this.state.band}"]`);
    const usBand = option ? option.dataset.us : null;
    return usBand ? usBand + this.state.cup : null;
  }

  /* "36C R2": the US/UK size exactly as the result shows it */
  get usFullSize() {
    return this.usSize ? this.usSize + ' ' + this.differenceCode : '';
  }

  resolve() {
    if (!this.isComplete) {
      this.showNothing();
      return;
    }

    /* Answering the finder supersedes any earlier manual or sister pick. */
    this.manualOverride = false;
    this.sisterVariant = null;

    const match = this.matchVariant();

    /* Report and log each size a customer lands on once; a genuine change of
       answer is a new size and reports again. */
    if (this.fullSize !== this.reportedSize) {
      this.reportedSize = this.fullSize;
      this.reportSize(match);
      this.logSize(match);
    }

    if (!match || !match.available) {
      this.showUnavailable(match);
      if (!this.isThreePlus) this.saveSize(match);
      return;
    }

    this.showResult();
    this.applyVariant();
    this.saveSize(match);
  }

  showNothing() {
    this.hasError = false;
    this.hide(this.resultEl);
    this.hide(this.errorEl);
    this.syncBuyVisibility();
  }

  showResult() {
    this.hasError = false;
    const suffix = ' ' + this.differenceCode;
    if (this.euEl) this.euEl.textContent = this.regularSize + suffix;
    if (this.usEl) this.usEl.textContent = (this.usSize || this.regularSize) + suffix;
    this.hide(this.errorEl);
    this.show(this.resultEl);
    this.syncBuyVisibility();
  }

  showUnavailable(match) {
    /* A signup confirmation belongs to the size it was made for. */
    if (this.notifiedSize !== this.fullSize) this.resetNotify();
    this.notifiedSize = this.fullSize;

    const reason = this.unavailableReason(match);
    const picks = this.sisterSizes();
    if (this.errorSizeEl) this.errorSizeEl.textContent = this.fullSize;
    if (this.errorHeadingEl) {
      let heading = reason === 'not_produced' ? this.dataset.headingMissing : this.dataset.headingSoldOut;
      if (this.isThreePlus) heading = this.dataset.headingThreePlus || heading;
      this.errorHeadingEl.textContent = heading;
    }
    /* 3+ cups is written in Merlin's voice, so it is signed (14.10). */
    if (this.signatureEl) this.signatureEl.hidden = !this.isThreePlus;
    /* A sold-out size says what she is waiting for (Anna-Maria, 28.09); a size not made keeps "Notify me". */
    let label = reason === 'not_produced' ? this.dataset.notifyLabelMissing : this.dataset.notifyLabelSoldOut;
    if (this.threePlusEmail) label = this.dataset.notifyLabelThreePlus || label;
    if (this.notifyEl && label) this.notifyEl.textContent = label;
    if (this.errorNoteEl) this.errorNoteEl.textContent = this.unavailableNote(reason, picks);
    /* On mobile step 2's tape link is out of sight by now, so 3+ cups repeats it here. */
    if (this.errorMeasureEl) this.errorMeasureEl.hidden = !this.isThreePlus;
    this.renderSisters(picks);
    this.hide(this.resultEl);
    this.show(this.errorEl);

    /*
     * Partial answers can match a variant on the way to an unavailable one
     * (75B L2 is valid while the customer is still heading for 75B L3), which
     * would leave that earlier variant selected and buyable underneath the
     * error. Lock the button until a valid size is chosen.
     */
    this.hasError = true;
    this.lockAddButton();
    this.syncBuyVisibility();
  }

  /* not_produced: no such variant. out_of_stock: the variant exists, sold out. */
  unavailableReason(match) {
    if (!match) return 'not_produced';
    return match.available ? '' : 'out_of_stock';
  }

  /*
   * size_found for every size the finder resolves, bought or not, so the
   * funnel has one step to count; size_unavailable as well for the dead ends.
   */
  reportSize(match) {
    const inStock = Boolean(match && match.available);
    this.track('size_found', { size: this.fullSize, us_size: this.usFullSize, in_stock: inStock });
    if (!inStock) {
      this.track('size_unavailable', { size: this.fullSize, reason: this.unavailableReason(match) });
    }
  }

  /*
   * Log every size a customer lands on, stocked or not, so demand for sizes we
   * don't carry is visible. Fire-and-forget: a failure here must never affect
   * the page. The rows go to the brain's /size-rows, which keeps only the
   * sheet's columns and stamps its own time; `id: 'DATETIME'` is the placeholder
   * SheetDB used for that, kept so the row reads the same either way.
   */
  logSize(match) {
    this.logRow({
      size: this.fullSize,
      in_stock: match && match.available ? 'yes' : 'no',
      us_size: this.usFullSize,
      reason: this.unavailableReason(match),
      gap_choice: SizeFinder.GAP_CHOICE[this.state.diff] || ''
    });
  }

  logRow(fields) {
    const url = this.dataset.logUrl;
    if (!url) return;

    const row = Object.assign(
      {
        size: '',
        id: 'DATETIME',
        xid: this.xid,
        age: '',
        underwear: '',
        currentBrands: '',
        leftBreast: '',
        rightBreast: '',
        bandSize: '',
        source: this.isLiveTheme ? 'finder' : 'test',
        in_stock: '',
        us_size: '',
        reason: '',
        gap_choice: ''
      },
      fields
    );

    SizeFinder.beacon(url, JSON.stringify({ data: [row] }));
  }

  /*
   * A beacon goes as text/plain, so the browser sends it without a CORS
   * preflight, and it is still delivered when the page is left straight after,
   * as it is on Add to cart. Nothing in the answer is needed.
   */
  static beacon(url, body) {
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url, body)) return;
    } catch (error) {
      // Fall back to fetch below.
    }
    fetch(url, { method: 'POST', mode: 'no-cors', keepalive: true, headers: { 'Content-Type': 'text/plain' }, body }).catch(
      (error) => {
        console.error('Size logging failed', error);
      }
    );
  }

  /*
   * Waitlist signup, with the same profile shape as the size calculator's: the
   * properties the finder does not know are sent empty.
   *
   * It goes to Klaviyo as an event (14.10), because a subscription records
   * marketing consent, and everyone with consent is in the segment the community
   * campaigns go to. The event records none: Klaviyo's flow Size Finder /
   * Waitlist signup puts her on the list for her reason and emails her size.
   * Community emails are the box under the address, unticked until she ticks
   * it, and only a tick subscribes her, to the community list. With no event
   * name set, she is subscribed to the reason list instead, as before.
   */
  submitNotify() {
    if (!this.emailEl || this.notifySending) return;

    const email = this.emailEl.value.trim();
    if (!email || !this.emailEl.checkValidity()) {
      this.showNotifyMessage(this.dataset.notifyInvalid, false);
      this.emailEl.focus();
      return;
    }

    const size = this.fullSize;
    const reason = this.unavailableReason(this.matchVariant());

    /* A preview adds nobody to a real list, unless the address is a +test one. */
    if (!this.isLiveTheme && !/\+test/i.test(email)) {
      this.showNotifyMessage('Preview theme: not sent to Klaviyo. Use a +test address to send.', true);
      return;
    }

    this.notifySending = true;
    this.notifyEl.disabled = true;

    const properties = {
      size: size,
      us_size: this.usFullSize,
      size_reason: reason,
      /*
       * Merlin's waitlist (23.09): she asked for this, so it is confirmed, and
       * waitingForSize is what her "your size is ready" notice reads.
       */
      size_notify: 'confirmed',
      waitingForSize: size,
      age: '',
      underwear: '',
      currentBrands: '',
      leftBreast: '',
      rightBreast: '',
      bandSize: '',
      currentBrand: '',
      /* Question 3 is the size she wears now, which the info@ alert shows as Current Size. */
      currentSize: this.regularSize
    };
    const gap = this.sizeGap(size);
    if (gap) properties.size_gap = gap;
    /* Which email she gets: three_plus, not_produced or out_of_stock. */
    properties.size_finder_case = this.isThreePlus ? 'three_plus' : reason;

    const community = Boolean(this.communityEl && this.communityEl.checked && this.dataset.notifyListCommunity);
    const requests = [];
    if (this.dataset.notifyMetric) {
      requests.push(
        this.joinWaitlist(email, properties, { community_emails: community ? 'yes' : 'no', source: 'size_finder' })
      );
    } else {
      /*
       * Only the list for this reason (not made, or sold out), which tells
       * production demand from stock demand. Not the shared list as well: its
       * "Measure again" flow would email her within 15 minutes, beside Merlin's
       * confirmation (charlottetown's diagnosis, 23.09). The shared list is the
       * fallback when no reason list is set.
       */
      const reasonList =
        reason === 'not_produced' ? this.dataset.notifyListMissing : this.dataset.notifyListSoldOut;
      const list = reasonList || this.dataset.notifyList;
      if (list) requests.push(this.subscribe(email, properties, list));
    }
    if (community) requests.push(this.subscribe(email, properties, this.dataset.notifyListCommunity));

    Promise.all(requests)
      .then((responses) => {
        responses.forEach((response) => {
          if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);
        });
        this.track('notify_signup', { size: size, reason: reason });
        if (community) this.track('community_signup', { source: 'size_finder' });
        const threePlusThanks = this.threePlusEmail && this.dataset.notifySuccessThreePlus;
        let success = threePlusThanks || this.dataset.notifySuccess || '';
        if (community && this.dataset.communityWelcome) success += ' ' + this.dataset.communityWelcome;
        this.showNotifyMessage(success.replace('[size]', size), true);
        /* Joined once is enough: the next size she signs up for asks again. */
        if (this.communityEl) this.communityEl.checked = false;
      })
      .catch((error) => {
        console.error('Back-in-stock signup failed', error);
        this.showNotifyMessage(this.dataset.notifyError, false);
      })
      .finally(() => {
        this.notifySending = false;
        if (this.notifyEl) this.notifyEl.disabled = false;
      });
  }

  /*
   * The waitlist event. Her size goes on her profile, as a subscription would put
   * it there; the event adds what is true of this signup only, such as whether
   * she ticked the community box, which is not true of her profile for good. The
   * email reads the event, so each signup is emailed its own size.
   *
   * unique_id is the size and this page view: Klaviyo records one event per
   * profile, metric and id, for good, so a retry after an error on this page
   * sends no second email, while another size, or the same size asked for again
   * on a later visit, gets its own (review rounds 1 and 2, 14.10).
   */
  joinWaitlist(email, properties, signup) {
    return fetch(
      `https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(this.dataset.notifyCompany)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-02-15' },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              metric: { data: { type: 'metric', attributes: { name: this.dataset.notifyMetric } } },
              properties: Object.assign({}, properties, signup),
              unique_id: 'waitlist ' + properties.size + ' ' + this.xid,
              profile: { data: { type: 'profile', attributes: { email: email, properties: properties } } }
            }
          }
        })
      }
    );
  }

  subscribe(email, properties, list) {
    return fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.dataset.notifyCompany)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-02-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email: email, properties: properties } } }
            },
            relationships: { list: { data: { type: 'list', id: list } } }
          }
        })
      }
    );
  }

  /* On success the form is replaced by the message; on failure it stays put so
     the customer can correct the address and retry. */
  showNotifyMessage(message, success) {
    if (!this.notifyMessageEl) return;
    this.notifyMessageEl.textContent = message;
    this.notifyMessageEl.hidden = false;
    this.notifyMessageEl.classList.toggle('pdp-oos__message--ok', success);
    if (this.notifyFormEl) this.notifyFormEl.hidden = success;
  }

  resetNotify() {
    if (this.notifyMessageEl) this.notifyMessageEl.hidden = true;
    if (this.notifyFormEl) this.notifyFormEl.hidden = false;
  }

  /*
   * The size this page would put in the cart: a manual pick, a chosen sister
   * size or the finder's own answer, as { id, o1, o2, available }.
   */
  get buyVariant() {
    if (this.manualOverride) {
      const current = this.variantSelects && this.variantSelects.currentVariant;
      return current ? { id: current.id, o1: current.option1, o2: current.option2, available: current.available } : null;
    }
    if (this.sisterVariant) return this.sisterVariant;
    return !this.hasError && this.isComplete ? this.matchVariant() || null : null;
  }

  /* US band for an EU size: "80C" -> "36C", as the result shows it. */
  usFor(size) {
    const parts = /^(\d+)(.*)$/.exec(String(size));
    if (!parts) return '';
    const band = this.sizeData.bands.find((entry) => String(entry.eu) === parts[1]);
    return band ? band.us + parts[2] : '';
  }

  /*
   * Keep the answers and the size they led to in this browser. `sister` marks a
   * chosen sister size, which is what the bar then offers. Storage can be
   * blocked (private windows, strict settings); the finder works regardless.
   */
  saveSize(variant, sister = false) {
    const product = this.dataset;
    const size = variant ? variant.o1 + ' ' + variant.o2 : this.fullSize;
    const saved = {
      v: SizeFinder.STORAGE_VERSION,
      side: this.state.side,
      diff: this.state.diff,
      band: this.state.band,
      cup: this.state.cup,
      size: size,
      us_size: variant ? [this.usFor(variant.o1), variant.o2].filter(Boolean).join(' ') : this.usFullSize,
      variant_id: variant ? variant.id : null,
      available: Boolean(variant && variant.available),
      price: variant && typeof variant.price === 'number' ? variant.price : null,
      currency: product.currency || '',
      sister: sister,
      title: product.productTitle || '',
      url: product.productUrl || '',
      image: product.productImage || ''
    };
    const previous = SizeFinder.readSaved();
    try {
      window.localStorage.setItem(SizeFinder.STORAGE_KEY, JSON.stringify(saved));
    } catch (error) {
      return;
    }
    window.dispatchEvent(
      new CustomEvent('symmetrista:size-saved', {
        detail: {
          saved: saved,
          /* The toast says it once: the first time a buyable size is kept. */
          toast: !this.restoring && saved.available && !(previous && previous.available)
        }
      })
    );
  }

  /*
   * Put back the answers kept in this browser, without reporting or logging
   * them: nobody answered anything on this page view. A kept sister choice is
   * chosen again if it is still offered.
   */
  restoreSize() {
    /* The size email's link names her size; it wins over whatever this browser kept. */
    const saved = this.sizeFromEmailLink() || SizeFinder.readSaved();
    if (!saved || !this.bandSelect || !this.cupSelect) return;
    const side = this.querySelector(`[data-finder-side="${saved.side}"]`);
    const diff = this.querySelector(`[data-finder-diff="${saved.diff}"]`);
    const bandOption = this.bandSelect.querySelector(`option[value="${saved.band}"]`);
    const cupOption = this.cupSelect.querySelector(`option[value="${saved.cup}"]`);
    if (!side || !diff || !bandOption || !cupOption) return;

    this.restoring = true;
    this.state = { side: saved.side, diff: saved.diff, band: saved.band, cup: saved.cup };
    this.selectPill(side, '[data-finder-side]');
    this.selectPill(diff, '[data-finder-diff], [data-finder-unsure]');
    this.bandSelect.value = saved.band;
    this.cupSelect.value = saved.cup;
    this.reportedSize = this.fullSize;
    this.maxStep = this.steps.length;
    this.goToStep(this.steps.length);
    this.resolve();

    if (saved.sister && saved.variant_id && this.sistersEl) {
      const button = this.sistersEl.querySelector(`[data-variant-id="${saved.variant_id}"]`);
      const pick = button && this.sisterSizes().find((entry) => String(entry.variant.id) === String(saved.variant_id));
      if (pick) this.chooseSister(pick, button);
    }
    this.restoring = false;
  }

  /*
   * Where she stands (Anna-Maria, 28.09): how far apart her sides are, as the
   * key Klaviyo's emails turn into a sentence with the share of women who
   * measured the same. The finder only knows the size: level 2 covers the same
   * cup and one cup apart, level 3 two cups. The shares stay in Klaviyo, never
   * on the page. Equal sides (L0) get none. 3+ cups (L4+) is three_plus, which
   * the waitlist email reads; the calculator, which measures, says three or
   * four_plus instead.
   */
  sizeGap(size) {
    const text = String(size || '').trim();
    if (/\b[LR]4\+$/.test(text)) return 'three_plus';
    const code = /\b[LR]([23])$/.exec(text);
    return code ? { 2: 'within_one', 3: 'two' }[code[1]] : '';
  }

  /*
   * The answers behind a variant, when the page was opened from the size email's
   * "see the bra in your size" link (utm_campaign=size_email). Only that link: a
   * catalogue ad also carries ?variant=, and must never fill in someone's size.
   */
  sizeFromEmailLink() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('utm_campaign') !== 'size_email') return null;
    const variant = this.variants.find((entry) => String(entry.id) === params.get('variant'));
    const size = variant && /^(\d+)([A-Z]+)$/.exec(variant.o1);
    const code = variant && /^([LR])(\d)$/.exec(variant.o2);
    if (!size || !code) return null;
    return { side: code[1], diff: code[2], band: size[1], cup: size[2] };
  }

  /*
   * "Not buying today? Email me my size." (Merlin, 15.09, items 6 and 9): under
   * Add to cart, never in its way, and never hiding the size. It sends the
   * profile to its own Klaviyo list with what the email needs to bring her back
   * to exactly this size.
   */
  get emailSizeEl() {
    const section = document.getElementById(`MainProduct-${this.dataset.section}`);
    return (section || document).querySelector('[data-email-size]');
  }

  watchEmailSize() {
    const box = this.emailSizeEl;
    if (!box) return;
    const input = box.querySelector('[data-email-size-input]');
    const submit = box.querySelector('[data-email-size-submit]');
    if (submit) submit.addEventListener('click', () => this.submitEmailSize());
    if (input) {
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.submitEmailSize();
        }
      });
    }
  }

  syncEmailSize(ready) {
    const box = this.emailSizeEl;
    if (!box) return;
    const variant = ready ? this.buyVariant : null;
    const size = variant ? variant.o1 + ' ' + variant.o2 : '';
    /* A confirmation belongs to the size it was sent for. */
    if (size !== this.emailedFor) {
      const form = box.querySelector('[data-email-size-form]');
      const message = box.querySelector('[data-email-size-message]');
      if (form) form.hidden = false;
      if (message) message.hidden = true;
    }
    box.hidden = !variant;
  }

  submitEmailSize() {
    const box = this.emailSizeEl;
    const variant = this.buyVariant;
    if (!box || !variant || this.emailSending) return;
    const input = box.querySelector('[data-email-size-input]');
    const submit = box.querySelector('[data-email-size-submit]');
    const email = input ? input.value.trim() : '';
    if (!email || !input.checkValidity()) {
      this.showEmailSizeMessage(box.dataset.invalid, false);
      if (input) input.focus();
      return;
    }

    const size = variant.o1 + ' ' + variant.o2;
    const usSize = [this.usFor(variant.o1), variant.o2].filter(Boolean).join(' ');

    if (!this.isLiveTheme && !/\+test/i.test(email)) {
      this.showEmailSizeMessage('Preview theme: not sent to Klaviyo. Use a +test address to send.', true);
      return;
    }

    const utm = 'utm_source=klaviyo&utm_medium=email&utm_campaign=size_email';
    const origin = window.location.origin;
    const properties = {
      size: size,
      us_size: usSize,
      variant_id: variant.id,
      in_stock: variant.available ? 'yes' : 'no',
      /* Straight into the cart with her size, and the product page with it chosen. */
      size_cart_link: `${origin}/cart/${variant.id}:1?${utm}`,
      size_product_link: `${origin}${this.dataset.productUrl || ''}?variant=${variant.id}&${utm}`
    };
    const gap = this.sizeGap(size);
    if (gap) properties.size_gap = gap;

    this.emailSending = true;
    if (submit) submit.disabled = true;
    this.subscribe(email, properties, box.dataset.list)
      .then((response) => {
        if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);
        this.emailedFor = size;
        this.track('size_email_signup', { size: size, us_size: usSize, in_stock: variant.available });
        this.showEmailSizeMessage(
          (box.dataset.success || '').replace('[size]', size).replace('[email]', email),
          true
        );
      })
      .catch((error) => {
        console.error('Email my size failed', error);
        this.showEmailSizeMessage(box.dataset.error, false);
      })
      .finally(() => {
        this.emailSending = false;
        if (submit) submit.disabled = false;
      });
  }

  showEmailSizeMessage(message, success) {
    const box = this.emailSizeEl;
    if (!box) return;
    const form = box.querySelector('[data-email-size-form]');
    const el = box.querySelector('[data-email-size-message]');
    if (el) {
      el.textContent = message || '';
      el.hidden = false;
      el.classList.toggle('pdp-email-size__message--ok', success);
    }
    if (form) form.hidden = success;
  }

  get addButton() {
    const form = document.getElementById(`product-form-${this.dataset.section}`);
    return form ? form.querySelector('[name="add"]') : null;
  }

  lockAddButton() {
    const addButton = this.addButton;
    if (!addButton) return;

    addButton.setAttribute('disabled', 'disabled');

    /*
     * Dawn re-enables the button asynchronously when the section fetch for the
     * previously applied variant lands, which can arrive after this lock. Hold
     * it shut for as long as the error is on screen.
     */
    if (this.buttonGuard) return;
    this.buttonGuard = new MutationObserver(() => {
      if (this.isLocked && !this.addButton.hasAttribute('disabled')) {
        this.addButton.setAttribute('disabled', 'disabled');
      }
    });
    this.buttonGuard.observe(addButton, { attributes: true, attributeFilter: ['disabled'] });
  }

  show(element) {
    if (element) element.hidden = false;
  }

  hide(element) {
    if (element) element.hidden = true;
  }

  /*
   * Drive the theme's variant dropdowns. Dispatching `change` on
   * <variant-selects> runs Dawn's own onVariantChange, so price, URL, the
   * hidden id input and the add-to-cart button all stay in sync.
   */
  applyVariant(option1 = this.regularSize, option2 = this.differenceCode, source = 'finder') {
    const selects = document.querySelector(
      `variant-selects[data-section="${this.dataset.section}"], variant-radios[data-section="${this.dataset.section}"]`
    );
    if (!selects) return;

    const dropdowns = Array.from(selects.querySelectorAll('select'));
    const band =
      selects.querySelector('select[name="options[Band]"]') || dropdowns[0];
    const cup =
      selects.querySelector('select[name="options[Cup]"]') || dropdowns[1];
    if (!band || !cup) return;

    band.value = option1;
    cup.value = option2;

    /* Flag so the manual-picker watcher doesn't mistake this for a user pick. */
    this.applyingVariant = true;
    selects.dispatchEvent(new Event('change', { bubbles: true }));
    this.applyingVariant = false;
    this.setSizeSource(source);
  }
}

/* The grid the finder falls back to if snippets/size-data.liquid is unreadable. */
SizeFinder.FALLBACK_SIZE_DATA = {
  bands: [
    { eu: '70', us: '32' },
    { eu: '75', us: '34' },
    { eu: '80', us: '36' },
    { eu: '85', us: '38' },
    { eu: '90', us: '40' },
    { eu: '95', us: '42' },
    { eu: '100', us: '44' }
  ],
  cups: [
    { eu: 'A', note: '' },
    { eu: 'B', note: '' },
    { eu: 'C', note: '' },
    { eu: 'D', note: '' },
    { eu: 'E', note: 'UK / US: DD' },
    { eu: 'F', note: 'UK: E / US: DDD' }
  ],
  sister_sizes: {}
};

/*
 * The kept size. The version guards against a shape this code no longer reads:
 * an old or foreign entry is ignored, never half-trusted.
 */
SizeFinder.STORAGE_KEY = 'symmetrista_size';
SizeFinder.STORAGE_VERSION = 1;

SizeFinder.readSaved = function () {
  try {
    const saved = JSON.parse(window.localStorage.getItem(SizeFinder.STORAGE_KEY) || 'null');
    if (!saved || saved.v !== SizeFinder.STORAGE_VERSION) return null;
    if (!['L', 'R'].includes(saved.side) || !['2', '3'].includes(String(saved.diff))) return null;
    if (!/^\d+$/.test(String(saved.band)) || !/^[A-Z]{1,3}$/.test(String(saved.cup))) return null;
    saved.diff = String(saved.diff);
    saved.band = String(saved.band);
    return saved;
  } catch (error) {
    return null;
  }
};

SizeFinder.clearSaved = function () {
  try {
    window.localStorage.removeItem(SizeFinder.STORAGE_KEY);
  } catch (error) {
    return;
  }
  window.dispatchEvent(new CustomEvent('symmetrista:size-saved', { detail: { saved: null, toast: false } }));
};

/*
 * "3+ cups" as a support level: one cup apart is level 2, two cups level 3, so
 * three or more is 4+, the calculator's L4 and up. No variant has it.
 */
SizeFinder.THREE_PLUS = '4+';

/* What the sheet's gap_choice column says for each answer to question 2. 3+ cups
   keeps the code its button has always logged. */
SizeFinder.GAP_CHOICE = { 2: '1_cup', 3: '2_cups', [SizeFinder.THREE_PLUS]: 'not_sure' };

customElements.define('size-finder', SizeFinder);
